import { Alert, Severity, AlertType } from '../../types/alerts';
import { Card, CardContent, CardHeader } from '../../../../ui/common-ui/card';
import { Badge } from "../../../../ui/common-ui/badge";
import { Button } from '../../../../ui/common-ui/button';
import { Switch } from '../../../../ui/common-ui/switch';
import { Checkbox } from '../../../../ui/common-ui/checkbox';
import { AlertTriangle, Shield, Brain, BookOpen, GitBranch, ChevronRight } from 'lucide-react';

interface AlertCardProps {
  alert: Alert;
  onViewDetails: (alert: Alert) => void;
  onToggle: (id: number, enabled: boolean) => void;
  onNavigateToPolicy?: (alert: Alert) => void;
  hideSwitch?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  hideActions?: boolean;
}

const getSeverityConfig = (severity: Severity) => {
  const configs = {
    Critical: { 
      className: 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400', 
      borderColor: 'border-l-red-500',
      icon: AlertTriangle, 
      label: 'Critical' 
    },
    High: { 
      className: 'bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400', 
      borderColor: 'border-l-orange-500',
      icon: AlertTriangle, 
      label: 'High' 
    },
    Medium: { 
      className: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600 dark:text-yellow-400', 
      borderColor: 'border-l-yellow-500',
      icon: AlertTriangle, 
      label: 'Medium' 
    },
    Low: { 
      className: 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400', 
      borderColor: 'border-l-green-500',
      icon: Shield, 
      label: 'Low' 
    },
  };
  const normalizedSeverity = (severity || 'Low').toString().charAt(0).toUpperCase() + (severity || 'Low').toString().slice(1).toLowerCase();
  return configs[normalizedSeverity as keyof typeof configs] || configs.Low;
};

const getAlertTypeConfig = (type: AlertType) => {
  const configs = {
    behaviour: { 
      icon: Brain, 
      label: 'Behaviour', 
      className: 'bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400' 
    },
    rule: { 
      icon: BookOpen, 
      label: 'Rule', 
      className: 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400' 
    },
    sequential: { 
      icon: GitBranch, 
      label: 'Sequential', 
      className: 'bg-primary/10 border-primary/30 text-primary' 
    },
  };
  const normalizedType = (type || 'behaviour').toString().toLowerCase();
  return configs[normalizedType as keyof typeof configs] || configs.behaviour;
};

export const AlertCard = ({ alert, onViewDetails, onToggle, onNavigateToPolicy, hideSwitch = false, selectable = false, selected = false, onSelect, hideActions = false }: AlertCardProps) => {
  const severityConfig = getSeverityConfig(alert.severity);
  const alertTypeConfig = getAlertTypeConfig(alert.type);
  const SeverityIcon = severityConfig.icon;
  const TypeIcon = alertTypeConfig.icon;

  return (
    <Card 
      onClick={() => selectable && onSelect && onSelect()}
      className={`
      group relative bg-card overflow-hidden rounded-2xl border border-border/50 border-l-4 ${severityConfig.borderColor}
      shadow-lg shadow-sky-500/5 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]
      transition-all duration-300 ease-out
      hover:-translate-y-px hover:border-primary/30 hover:border-l-primary
      ${!alert.isEnabled && !selectable ? 'opacity-60' : ''}
      ${selectable ? 'cursor-pointer' : ''}
    `}>
      <CardHeader className="pb-3">
        {/* Type & Severity Badges */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`${alertTypeConfig.className} w-fit font-medium text-xs transition-transform group-hover:scale-105`}
            >
              <TypeIcon className="h-3 w-3 mr-1" />
              {alertTypeConfig.label}
            </Badge>
            <Badge
              variant="outline"
              className={`${severityConfig.className} w-fit font-medium text-xs transition-transform group-hover:scale-105`}
            >
              <SeverityIcon className="h-3 w-3 mr-1" />
              {severityConfig.label}
            </Badge>
          </div>
          {selectable ? (
            <Checkbox
              checked={selected}
              onCheckedChange={onSelect}
              onClick={(e) => e.stopPropagation()}
              className={`data-[state=checked]:bg-primary transition-all duration-200`}
            />
          ) : !hideSwitch ? (
            <Switch
              checked={alert.isEnabled}
              onCheckedChange={(checked) => onToggle(alert.id, checked)}
              className="data-[state=checked]:bg-primary transition-all duration-200"
            />
          ) : null}
        </div>

        {/* Title */}
        <h3 className="font-semibold text-base text-foreground leading-tight mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-200">
          {alert.title}
        </h3>

        {/* Cloud Badge */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Shield className="h-3 w-3" />
          <span>{alert.cloud}</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        {/* Description */}
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
          {alert.info.what || 'No description available'}
        </p>

        {/* Steps for sequential alerts */}
        {alert.type === 'sequential' && alert.steps && Object.keys(alert.steps).length > 0 && (
          <div className="bg-muted/40 rounded-lg p-3 space-y-2 border border-border/50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Attack Chain</span>
              <Badge variant="secondary" className="text-xs h-5">
                {Object.keys(alert.steps).length} Steps
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 text-xs">
              {Object.entries(alert.steps).slice(0, 3).map(([step, action]) => (
                <span key={step} className="font-mono bg-background px-2 py-0.5 rounded border border-border/50 truncate max-w-[100px]">
                  {action.toString().split('.').pop()}
                </span>
              ))}
              {Object.keys(alert.steps).length > 3 && (
                <span className="font-semibold text-muted-foreground px-1">+{Object.keys(alert.steps).length - 3}</span>
              )}
            </div>
          </div>
        )}

        {/* Step for rule alerts */}
        {alert.type === 'rule' && alert.steps && Object.keys(alert.steps).length > 0 && (
          <div className="bg-muted/40 rounded-lg p-3 space-y-2 border border-border/50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Attack Step</span>
              <Badge variant="secondary" className="text-xs h-5">
                1 Step
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 text-xs">
              {Object.entries(alert.steps).slice(0, 1).map(([step, action]) => (
                <span key={step} className="font-mono bg-background px-2 py-0.5 rounded border border-border/50 truncate max-w-[150px]">
                  {action.toString().split('.').pop()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* MITRE Tags */}
        {alert.mitre.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {alert.mitre.slice(0, 2).map((technique, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="text-xs font-mono h-5 bg-muted/60"
              >
                {technique.code} • {technique.key}
              </Badge>
            ))}
            {alert.mitre.length > 2 && (
              <Badge variant="outline" className="text-xs h-5">
                +{alert.mitre.length - 2}
              </Badge>
            )}
          </div>
        )}

        {!hideActions && (
          <div className="flex gap-2 mt-3">
            {onNavigateToPolicy && (
              <Button
                onClick={() => onNavigateToPolicy(alert)}
                variant="outline"
                className="flex-1 rounded-2xl border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
              >
                <span>Create Policy</span>
              </Button>
            )}
            <Button
              onClick={() => onViewDetails(alert)}
              variant="outline"
              className="flex-1 rounded-2xl group/btn border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
            >
              <span>Details / Edit</span>
              <ChevronRight className="h-4 w-4 ml-auto transition-transform group-hover/btn:translate-x-1" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
